import { create } from 'zustand'
import { api } from '@/api/client'
import type { Event } from '@/types/models'
import { useSpaceStore } from './spaceStore'

interface EventState {
  events: Event[]
  loaded: boolean
  error: string | null
  loadEvents: () => Promise<void>
  addEvent: (evt: Omit<Event, 'id' | 'createdAt'>) => Promise<void>
  updateEvent: (id: string, data: Partial<Event>) => Promise<void>
  deleteEvent: (id: string, spaceId?: string) => Promise<void>
}

function getEventUrl(forSpaceId?: string): string {
  if (forSpaceId) return `/api/spaces/${forSpaceId}/events`
  const { activeSpace, currentSpace } = useSpaceStore.getState()
  if (activeSpace === 'default') return '/api/events'
  if (activeSpace === 'vault') return '/api/vault/events'
  if (activeSpace.startsWith('space:') && currentSpace) return `/api/spaces/${currentSpace.id}/events`
  return '/api/events'
}

export const useEventStore = create<EventState>()((set, get) => ({
  events: [],
  loaded: false,
  error: null,

  loadEvents: async () => {
    try {
      const url = getEventUrl()
      const eventsData = await api.get<Event[]>(url)
      set({ events: Array.isArray(eventsData) ? eventsData : [], loaded: true, error: null })
    } catch (e) {
      set({ loaded: true, error: e instanceof Error ? e.message : '加载失败' })
    }
  },

  addEvent: async (evt) => {
    const url = getEventUrl(evt.spaceId || undefined)
    await api.post(url, evt)
    await get().loadEvents()
  },

  updateEvent: async (id, data) => {
    const url = getEventUrl(data.spaceId || undefined)
    await api.patch(`${url}/${id}`, data)
    await get().loadEvents()
  },

  deleteEvent: async (id: string, spaceId?: string) => {
    const url = getEventUrl(spaceId)
    await api.delete(`${url}/${id}`)
    await get().loadEvents()
  },
}))
